import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RenderIf } from '@/components/RenderIf';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { CanvasMultiFilterDropdownProps } from '../types';
import { FilterButton } from '../FilterDropdown/styled';

// Multi-select filter backed by a repeated URL search param. Toggling an option
// adds/removes its value; the menu stays open on toggle so several can be
// picked at once. An empty selection clears the param entirely.
function CanvasMultiFilterDropdown({
    paramKey,
    label,
    icon: DimensionIcon,
    options,
}: CanvasMultiFilterDropdownProps) {
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedValues = useMemo(() => {
        const allowed = new Set(options.map(option => option.value));
        return searchParams.getAll(paramKey).filter(value => allowed.has(value));
    }, [searchParams, paramKey, options]);

    const selectedOptions = options.filter(option => selectedValues.includes(option.value));
    const hasSelection = selectedOptions.length > 0;
    const TriggerIcon = selectedOptions.length === 1 ? selectedOptions[0].icon : DimensionIcon;
    const summaryLabel =
        selectedOptions.length === 1
            ? selectedOptions[0].label
            : `${selectedOptions.length} selected`;

    const writeSelection = ({ values }: { values: string[] }) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete(paramKey);
        for (const value of values) {
            nextParams.append(paramKey, value);
        }
        setSearchParams(nextParams);
    };

    const handleToggle = ({ value, checked }: { value: string; checked: boolean }) => {
        const nextValues = checked
            ? [...selectedValues, value]
            : selectedValues.filter(current => current !== value);
        writeSelection({ values: nextValues });
    };

    const handleClear = () => {
        writeSelection({ values: [] });
    };

    return (
        <DropdownMenu>
            <FilterButton>
                <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                        <TriggerIcon size={14} />
                        <RenderIf isTrue={!hasSelection}>
                            <span>{label}</span>
                        </RenderIf>
                        <RenderIf isTrue={hasSelection}>
                            <span className="text-muted-foreground">{label}:</span>
                            <span>{summaryLabel}</span>
                            <span
                                role="button"
                                aria-label="Clear filter"
                                className="flex items-center cursor-pointer"
                                onPointerDown={event => event.stopPropagation()}
                                onClick={event => {
                                    event.stopPropagation();
                                    handleClear();
                                }}
                            >
                                <X size={14} />
                            </span>
                        </RenderIf>
                    </Button>
                </DropdownMenuTrigger>
            </FilterButton>
            <DropdownMenuContent align="end" className="w-56">
                {options.map(option => {
                    const OptionIcon = option.icon;
                    const isChecked = selectedValues.includes(option.value);
                    return (
                        <DropdownMenuCheckboxItem
                            key={option.value}
                            checked={isChecked}
                            onCheckedChange={checked =>
                                handleToggle({ value: option.value, checked: Boolean(checked) })
                            }
                            onSelect={event => event.preventDefault()}
                            className="gap-2"
                        >
                            <OptionIcon size={14} className="text-muted-foreground" />
                            <span className="flex-1">{option.label}</span>
                        </DropdownMenuCheckboxItem>
                    );
                })}
                <RenderIf isTrue={hasSelection}>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleClear} className="gap-2">
                        <X size={14} />
                        <span>Clear filter</span>
                    </DropdownMenuItem>
                </RenderIf>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default CanvasMultiFilterDropdown;
